import { useContext, useEffect } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { createActorContext } from '@xstate/react'
import multiCartMachine, { CART_MODAL_EVENT_NAME } from './multiCartMachine'
import type { ActorRefFrom } from 'xstate'
import type { MachineContext } from 'typings/app/machineContext'
import { useHistory } from 'react-router-dom'
import ModalContext from 'contexts/ModalContext'
import SharedCartsCartSelectionModal from 'components/Pages/UserCartPage/SharedCarts/Modals/SharedCartsCartSelectionModal'

// Cart machine is not needed at server side, initialise with server side state at client side, similar to redux.

export const MultiCartContext = createActorContext(multiCartMachine)

interface ReduxConnectedCartProviderProps {
  children: React.ReactNode
}

// Global reference to cart machine actor for Redux middleware access
let globalCartMachineActor: ActorRefFrom<typeof multiCartMachine> | null = null

export function getCartMachineActor() {
  return globalCartMachineActor
}

// Component to capture the actor reference
function CartMachineActorRegistry({ children }: { children: React.ReactNode }) {
  const actor = MultiCartContext.useActorRef()

  useEffect(() => {
    globalCartMachineActor = actor
    return () => {
      globalCartMachineActor = null
    }
  }, [actor])

  // Development-only state change logging
  useEffect(() => {
    if (actor) {
      const subscription = actor.subscribe((snapshot) => {
        console.debug('🛒 MultiCart Machine State Change')
        console.debug('State:', snapshot.value)
        console.debug('Context:', snapshot.context)
      })

      return () => {
        subscription.unsubscribe()
      }
    }
  }, [actor])

  return <>{children}</>
}

export function CartMachineModalListener({ children }: { children: React.ReactNode }) {
  const showModal = useContext(ModalContext)

  useEffect(() => {
    const onCartShowModalEvent = (e: CustomEvent<MachineContext.CartModalConfig>) => {
      // If we introduce more modal types, we can add a switch statement here to show the appropriate modal
      showModal(<SharedCartsCartSelectionModal multiCartItemIdsToMove={e.detail.multiCartItemIdsToMove} />)
    }

    document.addEventListener(CART_MODAL_EVENT_NAME, onCartShowModalEvent as any)

    return () => {
      document.removeEventListener(CART_MODAL_EVENT_NAME, onCartShowModalEvent as any)
    }
  }, [showModal])

  return <>{children}</>
}
export function MultiCartMachineProvider({ children }: ReduxConnectedCartProviderProps) {
  const intl = useIntl()
  const memberId = useAppSelector((state: App.State) => state.auth.account.memberId)
  const history = useHistory()
  const currentRegionCode = useAppSelector((state: App.State) => state.geo.currentRegionCode)
  const headlessMode = useAppSelector((state: App.State) => state.system.headlessMode)

  return (
    <MultiCartContext.Provider options={{ input: { memberId, history, currentRegionCode, headlessMode, intl } }}>
      <CartMachineActorRegistry>
        <CartMachineModalListener>
          {children}
        </CartMachineModalListener>
      </CartMachineActorRegistry>
    </MultiCartContext.Provider>
  )
}
